import { ref, watch } from "vue";
import type { ContributionType } from "@/types/contribution";

export interface ArchiveDraftPayload {
  query: string;
  selected?: {
    buildingId: string;
    name: string;
    district?: string;
    address?: string;
    publishBatch?: string;
    historicLevel?: string;
    matchedFields?: string;
    reason?: string;
    score?: number;
  };
  existingSections: Record<string, string>;
  sectionDrafts: Record<string, string>;
  sourceNote: string;
  changeNote: string;
}

export interface DraftData {
  id: string;
  type: ContributionType;
  title: string;
  content: string;
  locationName: string;
  longitude: number | null;
  latitude: number | null;
  eraText: string;
  sourceNote: string;
  authorName: string;
  contact: string;
  buildingId: string;
  savedAt: string;
  kind?: "contribution" | "building-archive";
  archive?: ArchiveDraftPayload;
}

const STORAGE_KEY = "jiangcheng_drafts";

function loadAll(): DraftData[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAll(drafts: DraftData[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
}

const drafts = ref<DraftData[]>(loadAll());

watch(drafts, (val) => saveAll(val), { deep: true });

export function useDrafts() {
  function save(draft: Omit<DraftData, "id" | "savedAt"> & { id?: string }) {
    const existing = drafts.value.findIndex((d) => d.id === draft.id);
    const entry: DraftData = {
      ...draft,
      id: draft.id || crypto.randomUUID(),
      savedAt: new Date().toISOString(),
    };
    if (existing >= 0) {
      drafts.value[existing] = entry;
    } else {
      drafts.value.unshift(entry);
    }
    return entry;
  }

  function remove(id: string) {
    drafts.value = drafts.value.filter((d) => d.id !== id);
  }

  function get(id: string): DraftData | undefined {
    return drafts.value.find((d) => d.id === id);
  }

  return { drafts, save, remove, get };
}
